import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Upload, AlertTriangle, CheckCircle2, ArrowRight, Download } from 'lucide-react';
import { apiFetch, ApiClientError, API_BASE, handleUnauthorized } from '@/lib/api';
import { downloadAsCsv } from '@/lib/csv-export';
import { toast } from '@/stores/toast';

/** `/imports/lite` (v1.0 Phase 3 Sprint 10、Lite版 月次PL/仕入先別コスト CSV取込)。
 *  設計: docs/design/v10-multi-tenant.md Chapter 4.2/4.2.1/4.3。
 *  ImportsPage.tsx (v0.1由来のマスタ/業務データCSV取込) とは対象テーブルが別のため独立ページ化。 */

type SourceType = 'monthly_pl' | 'monthly_supplier_costs';
type Layout = 'vertical' | 'horizontal';

type PreviewError = { row: number; column?: string; message: string };
type PreviewResponse =
  | { mapping_required: true; csv_source_type: SourceType; target_type: 'account_category' | 'supplier'; unmapped_names: string[] }
  | {
      mapping_required?: false;
      import_job_id: string;
      csv_source_type: SourceType;
      layout: Layout;
      total_rows: number;
      success_rows: number;
      error_rows: number;
      store_month_count: number;
      errors: PreviewError[];
    };

type CommitResponse = {
  import_job_id: string;
  status: 'completed' | 'failed';
  committed_store_months: number;
  total_store_months: number;
  error?: string;
};

const SOURCE_TYPE_LABEL: Record<SourceType, string> = {
  monthly_pl: '月次PL (勘定科目別)',
  monthly_supplier_costs: '月次仕入先別コスト',
};

const CSV_TEMPLATE_HELP: Record<SourceType, { vertical: string; horizontal: string }> = {
  monthly_pl: {
    vertical: '列: 店舗コード, 対象年月 (YYYY-MM), 勘定科目, 金額 — 1行 = 1店舗×1月×1科目',
    horizontal: '列: 店舗コード, 対象年月 (YYYY-MM), 家賃, 電気代, ... (勘定科目名を列見出しに) — 1行 = 1店舗×1月',
  },
  monthly_supplier_costs: {
    vertical: '列: 店舗コード, 対象年月 (YYYY-MM), 仕入先, 金額 — 1行 = 1店舗×1月×1仕入先',
    horizontal: '列: 店舗コード, 対象年月 (YYYY-MM), 〇〇食品, △△水産, ... (仕入先名を列見出しに) — 1行 = 1店舗×1月',
  },
};

const TARGET_LABEL: Record<SourceType, string> = {
  monthly_pl: '勘定科目',
  monthly_supplier_costs: '仕入先',
};

export function LiteCsvImportPage() {
  const [sourceType, setSourceType] = useState<SourceType>('monthly_pl');
  const [layout, setLayout] = useState<Layout>('vertical');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [commitResult, setCommitResult] = useState<CommitResponse | null>(null);

  const resetResults = () => { setPreview(null); setCommitResult(null); };

  // 雛形CSVダウンロード (縦持ち・横持ち両対応)。横持ちの列見出しはテナントごとに異なる
  // 勘定科目/仕入先名に依存するため、ダウンロード時にテナントの現行マスタを取得して埋め込む。
  const templateMut = useMutation({
    mutationFn: async () => {
      const endpoint = sourceType === 'monthly_pl' ? '/account-categories' : '/suppliers-lite';
      const master = await apiFetch<Array<{ name: string }>>(endpoint);
      const names = master.map((m) => m.name);
      const targetLabel = TARGET_LABEL[sourceType];
      if (layout === 'vertical') {
        const headers = ['店舗コード', '対象年月', targetLabel, '金額'];
        const exampleRows = names.length
          ? [{ 店舗コード: '', 対象年月: '2026-01', [targetLabel]: names[0], 金額: '' }]
          : [{ 店舗コード: '', 対象年月: '2026-01', [targetLabel]: '', 金額: '' }];
        downloadAsCsv(`lite_${sourceType}_vertical_template.csv`, headers, exampleRows);
      } else {
        if (names.length === 0) {
          throw new Error(`先に${targetLabel}マスタを1件以上登録してから横持ちの雛形をダウンロードしてください`);
        }
        const headers = ['店舗コード', '対象年月', ...names];
        downloadAsCsv(`lite_${sourceType}_horizontal_template.csv`, headers, [{ 店舗コード: '', 対象年月: '2026-01' }]);
      }
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : (e as Error).message),
  });

  const uploadMut = useMutation({
    mutationFn: async (): Promise<PreviewResponse> => {
      if (!file) throw new Error('ファイルを選択してください');
      const form = new FormData();
      form.append('file', file);
      form.append('layout', layout);
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API_BASE}/lite-csv-import/${sourceType}/preview`, {
        method: 'POST',
        body: form,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      handleUnauthorized(res.status);
      const body = await res.json();
      if (!res.ok || !body.success) {
        throw new ApiClientError(res.status, body.error?.code ?? 'UNKNOWN', body.error?.message ?? 'エラー');
      }
      return body.data;
    },
    onSuccess: (data) => { setPreview(data); setCommitResult(null); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : (e as Error).message),
  });

  const commitMut = useMutation({
    mutationFn: () => {
      if (!preview || preview.mapping_required) throw new Error('プレビューが未実行です');
      return apiFetch<CommitResponse>(`/lite-csv-import/${preview.import_job_id}/commit`, { method: 'POST' });
    },
    onSuccess: (data) => {
      setCommitResult(data);
      if (data.status === 'completed') toast.success(`取込が完了しました (${data.committed_store_months}件の店舗×月分)`);
      else toast.error(`取込に失敗しました: ${data.error ?? ''}`);
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : (e as Error).message),
  });

  return (
    <div className="space-y-4 max-w-3xl">
      <div>
        <h2 className="text-h2">Lite版データ取込</h2>
        <p className="text-sm text-neutral-sub mt-1">月次PL・仕入先別コストをCSVから取込みます (Lite版データモデル)。取込は対象の店舗×月を丸ごと置き換えます。</p>
      </div>

      <div className="border border-neutral-line rounded-lg bg-white p-4 space-y-4">
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-sm text-neutral-sub mb-1">取込データ種別</label>
            <select
              value={sourceType}
              onChange={(e) => { setSourceType(e.target.value as SourceType); resetResults(); }}
              className="px-3 py-2 rounded border border-neutral-line focus-ring text-sm"
            >
              {(Object.keys(SOURCE_TYPE_LABEL) as SourceType[]).map((t) => (
                <option key={t} value={t}>{SOURCE_TYPE_LABEL[t]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-neutral-sub mb-1">CSVレイアウト</label>
            <div className="flex gap-2">
              {(['vertical', 'horizontal'] as Layout[]).map((l) => (
                <button key={l} type="button" onClick={() => { setLayout(l); resetResults(); }}
                  className={`px-3 py-2 rounded border text-sm ${layout === l ? 'border-brand-red text-brand-red bg-surface-sunken' : 'border-neutral-line text-neutral-sub'}`}>
                  {l === 'vertical' ? '縦持ち' : '横持ち'}
                </button>
              ))}
            </div>
          </div>
        </div>

        <p className="text-xs text-neutral-sub bg-surface-sunken rounded px-3 py-2">
          {CSV_TEMPLATE_HELP[sourceType][layout]}
        </p>

        <button type="button" onClick={() => templateMut.mutate()} disabled={templateMut.isPending}
          className="flex items-center gap-1 px-3 py-1.5 rounded border border-neutral-line text-sm text-neutral-sub hover:bg-surface-sunken disabled:opacity-50">
          <Download size={14} /> {templateMut.isPending ? '準備中…' : 'この設定で雛形CSVをダウンロード'}
        </button>

        <div>
          <label className="block text-sm text-neutral-sub mb-1">CSVファイル (10MB以下、UTF-8またはShift_JIS)</label>
          <input type="file" accept=".csv"
            onChange={(e) => { setFile(e.target.files?.[0] ?? null); resetResults(); }}
            className="text-sm" />
        </div>

        <button type="button" onClick={() => uploadMut.mutate()} disabled={!file || uploadMut.isPending}
          className="flex items-center gap-1 px-4 py-2 rounded bg-brand-red text-white text-sm hover:bg-brand-red-hover disabled:opacity-50">
          <Upload size={16} /> {uploadMut.isPending ? '解析中…' : 'アップロード・プレビュー'}
        </button>
      </div>

      {preview?.mapping_required && (
        <div className="border border-alert-border bg-alert-bg rounded-lg p-4 space-y-2">
          <div className="flex items-center gap-2 text-alert-text font-semibold">
            <AlertTriangle size={18} /> マッピング未設定の項目があります
          </div>
          <p className="text-sm text-alert-text">
            以下の{preview.target_type === 'account_category' ? '勘定科目名' : '仕入先名'}は、CSV列名マッピングが未設定のため取込めません。
            先に「CSV列名マッピング」画面で登録してから再度取込んでください。
          </p>
          <ul className="text-sm list-disc list-inside">
            {preview.unmapped_names.map((n) => <li key={n}>{n}</li>)}
          </ul>
          <Link to="/settings/csv-column-mappings" className="inline-flex items-center gap-1 text-sm text-brand-red hover:text-brand-red-hover font-medium">
            CSV列名マッピング画面へ <ArrowRight size={14} />
          </Link>
        </div>
      )}

      {preview && !preview.mapping_required && (
        <div className="border border-neutral-line rounded-lg bg-white p-4 space-y-3">
          <h3 className="font-semibold text-sm">プレビュー結果</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div><div className="text-neutral-sub">総行数</div><div className="font-semibold">{preview.total_rows}</div></div>
            <div><div className="text-neutral-sub">取込可能</div><div className="font-semibold text-success-text">{preview.success_rows}</div></div>
            <div><div className="text-neutral-sub">エラー</div><div className="font-semibold text-alert-text">{preview.error_rows}</div></div>
            <div><div className="text-neutral-sub">対象店舗×月</div><div className="font-semibold">{preview.store_month_count}</div></div>
          </div>

          {preview.errors.length > 0 && (
            <div className="border border-neutral-line rounded overflow-x-auto max-h-64 overflow-y-auto">
              <table className="w-full min-w-[420px] text-xs">
                <thead className="bg-neutral-panel text-neutral-sub sticky top-0">
                  <tr><th className="text-left px-3 py-1.5">行</th><th className="text-left px-3 py-1.5">列</th><th className="text-left px-3 py-1.5">エラー内容</th></tr>
                </thead>
                <tbody>
                  {preview.errors.map((e, i) => (
                    <tr key={i} className="border-t border-neutral-line">
                      <td className="px-3 py-1.5">{e.row}</td>
                      <td className="px-3 py-1.5">{e.column ?? '-'}</td>
                      <td className="px-3 py-1.5">{e.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {preview.success_rows > 0 && !commitResult && (
            <button type="button" onClick={() => commitMut.mutate()} disabled={commitMut.isPending}
              className="px-4 py-2 rounded bg-brand-red text-white text-sm hover:bg-brand-red-hover disabled:opacity-50">
              {commitMut.isPending ? '取込中…' : `この内容で取込む (${preview.store_month_count}件の店舗×月分)`}
            </button>
          )}
        </div>
      )}

      {commitResult && (
        <div className={`border rounded-lg p-4 flex items-start gap-2 ${commitResult.status === 'completed' ? 'border-success-border bg-success-bg text-success-text' : 'border-danger-border bg-danger-bg text-danger-text'}`}>
          <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
          <div className="text-sm">
            {commitResult.status === 'completed'
              ? `取込が完了しました (${commitResult.committed_store_months}件の店舗×月分)`
              : `一部のみ取込まれました (${commitResult.committed_store_months}/${commitResult.total_store_months}件)。${commitResult.error ?? ''}`}
          </div>
        </div>
      )}
    </div>
  );
}
